import { useRef, useCallback } from 'react';

/**
 * Custom hook for undo/redo functionality
 * Manages version history and provides clean API for state restoration
 */
export const useUndoRedo = ({ maxHistorySize = 20 }) => {
  const undoStack = useRef([]);
  const redoStack = useRef([]);

  const serializeState = useCallback((state) => {
    // Add safety check for undefined state
    if (!state || !state.nodes || !state.relationships) {
      console.warn('Invalid state passed to serializeState:', state);
      return {
        nodes: [],
        relationships: [],
        root: null,
        selectedId: null,
        viewport: {
          scale: 1,
          offset: { x: 0, y: 0 },
        },
      };
    }

    // Deep-clone core graph state so history entries are immutable
    const clonedNodes = JSON.parse(JSON.stringify(state.nodes));
    const clonedRelationships = JSON.parse(JSON.stringify(state.relationships));

    // Optional viewport (pan/zoom) state is stored as-is so that
    // undo can restore the user's local view. This remains a
    // per-user concern and is not synced via Yjs.
    let viewport = undefined;
    if (state.viewport && typeof state.viewport === 'object') {
      const scale =
        typeof state.viewport.scale === 'number' &&
        Number.isFinite(state.viewport.scale)
          ? state.viewport.scale
          : 1;

      const rawOffset = state.viewport.offset || {};
      const offset =
        typeof rawOffset.x === 'number' &&
        typeof rawOffset.y === 'number' &&
        Number.isFinite(rawOffset.x) &&
        Number.isFinite(rawOffset.y)
          ? { x: rawOffset.x, y: rawOffset.y }
          : { x: 0, y: 0 };

      viewport = { scale, offset };
    }

    return {
      nodes: clonedNodes,
      relationships: clonedRelationships,
      root: state.root,
      selectedId: state.selectedId,
      viewport,
    };
  }, []);

  const saveVersion = useCallback((state) => {
    // Add safety check at the start
    if (!state) {
      console.warn('saveVersion called with undefined state');
      return;
    }

    const serializedState = serializeState(state);
    const stack = undoStack.current;

    // Check if current state is different from last saved state
    const lastVersion = stack.length > 0 ? stack[stack.length - 1] : null;
    if (lastVersion && JSON.stringify(lastVersion) === JSON.stringify(serializedState)) {
      return; // No changes, don't save
    }

    // Add new version to stack
    let newStack = [...stack, serializedState];

    // Trim stack if it exceeds max size
    if (newStack.length > maxHistorySize) {
      const excess = newStack.length - maxHistorySize;
      newStack = newStack.slice(excess);
    }

    undoStack.current = newStack;
    // A new user action creates a new history branch. States that were
    // previously available through redo are no longer reachable.
    redoStack.current = [];
  }, [serializeState, maxHistorySize]);

  const appendBounded = useCallback((stack, state) => {
    if (!state) return stack;
    const lastState = stack.length > 0 ? stack[stack.length - 1] : null;
    if (lastState && JSON.stringify(lastState) === JSON.stringify(state)) {
      return stack;
    }
    const nextStack = [...stack, state];
    return nextStack.length > maxHistorySize
      ? nextStack.slice(nextStack.length - maxHistorySize)
      : nextStack;
  }, [maxHistorySize]);

  const undo = useCallback((currentState) => {
    const stack = undoStack.current;

    if (stack.length === 0) {
      console.log("Nothing to undo");
      return null;
    }

    // Get the most recent saved state (represents the state *before*
    // the last logical change) and remove it from the history stack.
    const previousState = stack[stack.length - 1];

    if (!previousState) {
      console.log("No previous state to undo to");
      return null;
    }

    if (currentState) {
      redoStack.current = appendBounded(
        redoStack.current,
        serializeState(currentState)
      );
    }
    undoStack.current = stack.slice(0, stack.length - 1);

    console.log("Undo successful");
    return previousState;
  }, [appendBounded, serializeState]);

  const redo = useCallback((currentState) => {
    const stack = redoStack.current;

    if (stack.length === 0) {
      console.log("Nothing to redo");
      return null;
    }

    const nextState = stack[stack.length - 1];
    if (!nextState) return null;

    if (currentState) {
      undoStack.current = appendBounded(
        undoStack.current,
        serializeState(currentState)
      );
    }
    redoStack.current = stack.slice(0, stack.length - 1);

    console.log("Redo successful");
    return nextState;
  }, [appendBounded, serializeState]);

  const canUndo = useCallback(() => {
    return undoStack.current.length > 0;
  }, []);

  const canRedo = useCallback(() => {
    return redoStack.current.length > 0;
  }, []);

  const getHistorySize = useCallback(() => {
    return undoStack.current.length;
  }, []);

  const getRedoHistorySize = useCallback(() => {
    return redoStack.current.length;
  }, []);

  const clearHistory = useCallback(() => {
    undoStack.current = [];
    redoStack.current = [];
  }, []);

  return {
    saveVersion,
    undo,
    redo,
    canUndo,
    canRedo,
    getHistorySize,
    getRedoHistorySize,
    clearHistory
  };
};
